import React, { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, SearchX } from "lucide-react";
import { Pagination } from "./Pagination";
import { SearchInput } from "./Form";
import { EmptyState, TableSkeleton } from "./States";
import { cx } from "./format";

export const getField = (row, field) => {
  if (!field || row == null) return undefined;
  if (!field.includes(".")) return row[field];
  return field.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), row);
};

const compare = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const na = Number(a), nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb) && a !== "" && b !== "") return na - nb;
  const da = Date.parse(a), db = Date.parse(b);
  if (typeof a === "string" && /\d{4}-\d{2}-\d{2}/.test(a) && !Number.isNaN(da) && !Number.isNaN(db)) return da - db;
  return String(a).localeCompare(String(b), "en-IN", { numeric: true, sensitivity: "base" });
};

/**
 * The one table for every list in the portal.
 *
 * columns: [{ field, title, render?(row, {rowIndex}), sortable?, align?, width?,
 *             className?, headerClassName?, cellStyle?, headerStyle?, lookup?, searchable? }]
 * Search, sort and pagination are client-side; pass `total` + `onPageChange`
 * (server mode) when the API pages for you.
 */
export const DataTable = ({
  columns = [], data: rawData = [], rowKey = "_id", title, description, toolbar, toolbarRight,
  search = true, searchPlaceholder = "Search…", onSearchChange, searchValue,
  sortable = true, paginate = true, pageSize: initialPageSize = 10, pageSizeOptions = [10, 20, 50],
  loading = false, onRowClick, selectable = false, selectedRows, onSelectionChange,
  emptyTitle = "No records found", emptyDescription, emptyAction, rowClassName, dense = false,
  stickyHeader = false, className, footer,
  // server-side paging (optional)
  total: serverTotal, page: serverPage, onPageChange: serverOnPageChange, onPageSizeChange: serverOnPageSizeChange,
}) => {
  const data = Array.isArray(rawData) ? rawData : [];
  const [query, setQuery] = useState(searchValue ?? "");
  const [sort, setSort] = useState(null); // { field, dir }
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [internalSelection, setInternalSelection] = useState([]);
  const selection = selectedRows ?? internalSelection;
  const server = typeof serverOnPageChange === "function";

  useEffect(() => { if (searchValue !== undefined) setQuery(searchValue); }, [searchValue]);
  useEffect(() => { setPageSize(initialPageSize); }, [initialPageSize]);

  const setSelection = (next) => {
    if (selectedRows === undefined) setInternalSelection(next);
    onSelectionChange?.(next);
  };

  const cellText = (row, col) => {
    const v = getField(row, col.field);
    if (col.lookup) return col.lookup[v] ?? v;
    return v;
  };

  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q || server) return data;
    const cols = columns.filter((c) => c.field && c.searchable !== false);
    return data.filter((row) =>
      cols.some((c) => {
        const v = cellText(row, c);
        return v != null && typeof v !== "object" && String(v).toLowerCase().includes(q);
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, query, columns, server]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => (c.sortField || c.field) === sort.field);
    const get = col?.sortValue || ((row) => (col?.lookup ? cellText(row, col) : getField(row, sort.field)));
    const out = [...filtered].sort((a, b) => compare(get(a), get(b)));
    return sort.dir === "desc" ? out.reverse() : out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort, columns]);

  const total = server ? serverTotal ?? data.length : sorted.length;
  const currentPage = server ? serverPage ?? 0 : page;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { if (!server && page > pageCount - 1) setPage(0); }, [server, page, pageCount]);

  const rows = paginate && !server ? sorted.slice(page * pageSize, page * pageSize + pageSize) : sorted;
  const offset = paginate ? currentPage * pageSize : 0;

  const toggleSort = (col) => {
    const field = col.sortField || col.field;
    setSort((s) => (!s || s.field !== field ? { field, dir: "asc" } : s.dir === "asc" ? { field, dir: "desc" } : null));
  };

  const keyOf = (row, i) => getField(row, rowKey) ?? row.id ?? i;
  const selectedKeys = new Set(selection.map((r, i) => keyOf(r, i)));
  const allOnPageSelected = rows.length > 0 && rows.every((r, i) => selectedKeys.has(keyOf(r, offset + i)));

  const togglePage = () => {
    if (allOnPageSelected) {
      const pageKeys = new Set(rows.map((r, i) => keyOf(r, offset + i)));
      setSelection(selection.filter((r, i) => !pageKeys.has(keyOf(r, i))));
    } else {
      const merged = [...selection];
      rows.forEach((r, i) => { if (!selectedKeys.has(keyOf(r, offset + i))) merged.push(r); });
      setSelection(merged);
    }
  };
  const toggleRow = (row, i) => {
    const k = keyOf(row, i);
    setSelection(selectedKeys.has(k) ? selection.filter((r, j) => keyOf(r, j) !== k) : [...selection, row]);
  };

  const handleSearch = (v) => {
    setQuery(v);
    setPage(0);
    onSearchChange?.(v);
  };

  const showHeader = title || description || search || toolbar || toolbarRight;
  const pad = dense ? "px-3 py-2" : "px-4 py-3";

  return (
    <div className={cx("bg-cm-surface border border-cm-border rounded-cm-lg min-w-0", className)}>
      {showHeader && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-cm-border">
          {(title || description) && (
            <div className="min-w-0 mr-auto">
              {title && <h3 className="text-base font-bold text-cm-text m-0">{title}</h3>}
              {description && <p className="text-[13px] text-cm-muted m-0 mt-0.5">{description}</p>}
            </div>
          )}
          <div className={cx("flex flex-wrap items-center gap-2", !(title || description) && "flex-1")}>
            {toolbar}
            {search && (
              <SearchInput value={query} onChange={handleSearch} placeholder={searchPlaceholder} size="sm" className="w-full sm:w-60" />
            )}
          </div>
          {toolbarRight && <div className="flex flex-wrap items-center gap-2 ml-auto">{toolbarRight}</div>}
        </div>
      )}

      <div className={cx("overflow-x-auto", stickyHeader && "max-h-[70vh] overflow-y-auto")}>
        {loading && !data.length ? (
          <TableSkeleton cols={Math.min(columns.length || 4, 6)} />
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead className={cx(stickyHeader && "sticky top-0 z-[1]")}>
              <tr>
                {selectable && (
                  <th className={cx("w-10 bg-cm-surface border-b border-cm-border", pad)}>
                    <input
                      type="checkbox"
                      aria-label="Select all rows on this page"
                      checked={allOnPageSelected}
                      onChange={togglePage}
                      className="w-4 h-4 accent-[var(--cm-gold)] cursor-pointer align-middle"
                    />
                  </th>
                )}
                {columns.map((col, ci) => {
                  const canSort = sortable && col.sortable !== false && col.sorting !== false && (col.field || col.sortValue);
                  const active = sort && sort.field === (col.sortField || col.field);
                  const alignCls = col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left";
                  return (
                    <th
                      key={col.field || ci}
                      scope="col"
                      style={{ width: col.width, ...col.headerStyle }}
                      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                      className={cx(
                        "bg-cm-surface border-b border-cm-border text-xs font-semibold text-cm-muted whitespace-nowrap",
                        pad, alignCls, col.headerClassName,
                      )}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(col)}
                          className={cx(
                            "inline-flex items-center gap-1 hover:text-cm-text",
                            col.align === "right" && "flex-row-reverse",
                            active && "text-cm-text",
                          )}
                        >
                          {col.title ?? col.field}
                          {active ? (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-40" />}
                        </button>
                      ) : (
                        col.title ?? col.field
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const absoluteIndex = offset + i;
                const k = keyOf(row, absoluteIndex);
                const isSelected = selectable && selectedKeys.has(k);
                return (
                  <tr
                    key={k}
                    onClick={onRowClick ? (e) => onRowClick(e, row) : undefined}
                    className={cx(
                      "border-b border-cm-border last:border-b-0 transition-colors",
                      onRowClick && "cursor-pointer",
                      isSelected ? "bg-cm-gold-soft" : "hover:bg-cm-surface-2",
                      typeof rowClassName === "function" ? rowClassName(row) : rowClassName,
                    )}
                  >
                    {selectable && (
                      <td className={pad} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label="Select row"
                          checked={isSelected}
                          onChange={() => toggleRow(row, absoluteIndex)}
                          className="w-4 h-4 accent-[var(--cm-gold)] cursor-pointer align-middle"
                        />
                      </td>
                    )}
                    {columns.map((col, ci) => {
                      const alignCls = col.align === "right" ? "text-right tabular-nums" : col.align === "center" ? "text-center" : "";
                      const content = col.render ? col.render(row, { rowIndex: absoluteIndex }) : cellText(row, col);
                      return (
                        <td
                          key={col.field || ci}
                          style={col.cellStyle}
                          className={cx(pad, "text-cm-text align-middle", alignCls, col.className, col.bodyClassName)}
                        >
                          {content === null || content === undefined || content === "" ? <span className="text-cm-faint">—</span> : content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && rows.length === 0 && (
          query ? (
            <EmptyState
              compact
              icon={SearchX}
              title={`No matches for “${query}”`}
              description="Try a different name, phone number or ID."
            />
          ) : (
            <EmptyState compact title={emptyTitle} description={emptyDescription} action={emptyAction} />
          )
        )}
        {loading && data.length > 0 && (
          <div className="h-0.5 w-full overflow-hidden bg-cm-surface-2"><div className="h-full w-1/3 bg-cm-gold animate-pulse" /></div>
        )}
      </div>

      {paginate && total > 0 && (
        <Pagination
          page={currentPage}
          pageSize={pageSize}
          total={total}
          pageSizeOptions={pageSizeOptions}
          onPageChange={(p) => (server ? serverOnPageChange(p) : setPage(p))}
          onPageSizeChange={(n) => {
            setPageSize(n);
            if (server) serverOnPageSizeChange?.(n);
            else setPage(0);
          }}
        />
      )}
      {footer}
    </div>
  );
};

export default DataTable;
